import { useTheme } from "@emotion/react";
import { createStackNavigator } from "@react-navigation/stack";
import React, { useContext, useEffect } from "react";
import CategoryListItem from "../../components/CategoryListItem";
import DrawerButton from "../../components/DrawerButton";
import { tokenizedText } from "../../components/RichText";
import StrategiesHero from "../../components/StategiesHero";
import Text from "../../components/Text";
import { FurnitureContext } from "../../utils/furniture";
import { template } from "../../utils/locale";
import { NavContext } from "../../utils/nav";
import { QuizOrderContext } from "../../utils/quiz";
import { StrategyCategoryOrderContext } from "../../utils/strategy";
import { AppTheme } from "../../utils/theme";
import Strategy from "../Strategy";
import StrategyCategory from "../StrategyCategory";
import { Background, Body, CategoryList, Container, Title } from "./styles";

const Stack = createStackNavigator();

const MyStrategiesNavigator = ({ navigation }) => {
    const { backgroundPattern }: AppTheme = useTheme() as AppTheme;

    return (
        <Container>
            <Background source={backgroundPattern} />
            <DrawerButton />
            <Stack.Navigator
                screenOptions={{ headerShown: false, animationEnabled: true }}
                initialRouteName="MyStrategies">
                <Stack.Screen name="MyStrategies" component={MyStrategies} />
                <Stack.Screen
                    name="StrategyCategory"
                    component={StrategyCategory}
                />
                <Stack.Screen name="Strategy" component={Strategy} />
            </Stack.Navigator>
        </Container>
    );
};

const MyStrategies = ({ navigation }) => {
    const { setLeafNav } = useContext(NavContext);
    const quizOrder = useContext(QuizOrderContext);
    const {
        myStrategiesTitle,
        strings: {
            myStrategiesTitleNotYetCustomized,
            myStrategiesTitleCompletelyCustomized,
            myStrategiesTitlePercentageCustomized,
            myStrategiesCaptionFirstQuiz,
            myStrategiesCaptionNextQuiz,
        },
    } = useContext(FurnitureContext);
    const strategyCategoryOrder = useContext(StrategyCategoryOrderContext);

    useEffect(() => {
        setLeafNav(navigation);
    }, [navigation]);

    if (!quizOrder || !strategyCategoryOrder) {
        return (
            <Container>
                <Text>Loading...</Text>
            </Container>
        );
    }

    const getPercentageCustomizedTitle: (valuse: {
        percentage: number;
    }) => string = template(myStrategiesTitlePercentageCustomized);

    const getFirstQuizCaption: (values: { title: string }) => string = template(
        myStrategiesCaptionFirstQuiz,
    );

    const getNextQuizCaption: (values: { title: string }) => string = template(
        myStrategiesCaptionNextQuiz,
    );

    const percentage = Math.floor(
        (quizOrder.completed.length / quizOrder.available.length) * 100,
    );

    let heroTitle: string;
    let heroCaption: string;
    let quizTitleRich = quizOrder?.next?.titleRich;
    let quizTitle = quizOrder?.next?.title;
    const theTitle = quizTitleRich
        ? tokenizedText({ doc: quizTitleRich })
        : quizTitle;

    if (quizOrder.completed.length === 0) {
        heroTitle = myStrategiesTitleNotYetCustomized;
        heroCaption = getFirstQuizCaption({ title: theTitle });
    } else if (!quizOrder.next) {
        heroTitle = myStrategiesTitleCompletelyCustomized;
    } else {
        heroTitle = getPercentageCustomizedTitle({ percentage });
        heroCaption = getNextQuizCaption({ title: theTitle });
    }

    return (
        <Container>
            {/* scrollIndicatorInsets hack for iOS 13 */}
            <Body scrollIndicatorInsets={{ right: 1 }}>
                <Title type="h3">{myStrategiesTitle}</Title>
                <StrategiesHero
                    cta={heroTitle}
                    details={heroCaption}
                    percentage={percentage}
                    onPress={() =>
                        navigation.navigate("Home", {
                            screen: "Quiz",
                            params: {
                                quizId: quizOrder.next._id,
                            },
                        })
                    }
                />
                <CategoryList>
                    {strategyCategoryOrder.available.map(strategyCategory => (
                        <CategoryListItem
                            key={strategyCategory._id}
                            item={strategyCategory}
                            icon={strategyCategory.icon}
                            count={strategyCategory.strategies.length}
                            hasNew={strategyCategory?._meta?.hasNew}
                            onPress={() =>
                                navigation.navigate("StrategyCategory", {
                                    strategyCategoryId: strategyCategory._id,
                                })
                            }
                            singular="strategy"
                            plural="strategies"
                        />
                    ))}
                </CategoryList>
            </Body>
        </Container>
    );
};
export default MyStrategiesNavigator;
